import axios from "axios";
import tokenService from "utils/tokenService";
import authApis from "apis/authApis";
import Config from "../config";
import { getErrorMessage } from './errorMessages';
import { t } from 'i18next';
import * as notification from '../utils/notification';

export const BASE_URL =  Config.mainApiUrl;
const TokenService = tokenService.getService();

const requestConfig = {
  method: "get",
  headers: {
    "Content-Type": "application/json",
    Accept: "text/plain",
  },
  maxRedirects: 0,
  // timeout: 9000,
};

let instance = axios.create(requestConfig);

instance.interceptors.request.use(function (config) {
  const accessToken = TokenService.getAccessToken();
  config.headers["Platform"] = "web";

  if (accessToken) {
    config.headers["Authorization"] = `Bearer ${accessToken}`;
  }
  config.params = Object.assign({}, config.params || {});
  return config;
});

const getDispatch = (url, payload) => {
  return instance.get(url, payload);
};

const postDispatch = (url, payload) => {
  let payLoad;

  payLoad = payload;

  return instance.post(url, payLoad);
};

const patchDispatch = (url, payload) => {
  return instance.patch(url, payload);
};

const putDispatch = (url, payload) => {
  return instance.put(url, payload);
};

const deleteDispatch = (url, payload) => {
  return instance.delete(url, payload);
};

function generateUrl (partialUrl, params = null) {
  let url = BASE_URL + partialUrl;
  let postFix = "";
  if (!params) return url;
  Object.keys(params).map((key) => {
    if (!params[key]) return;
    let searchKey = "{" + key + "}";
    if (url.indexOf(searchKey) !== -1) {
      url = url.replace(searchKey, params[key]);
    } else {
      if (!postFix) {
        postFix = "?";
      } else {
        postFix += "&";
      }
      postFix += key + "=" + params[key];
    }
  });
  return url + postFix;
}

const logout = () => {
  authApis
    .logout({})
    .then(() => {
      notification.error({ message: t('EXPIRED_TOKEN') });
      TokenService.clearToken();
      window.location.replace(window.location.origin + "/#/login");
    })
    .catch((error) => {
      notification.error({
        message: error?.response?.data?.message || error.response.data.error.message
			});
    });
};

/**
 * Clears tokens from the browser and redirects user to the login page
 * @param {object} [options] - Some options to customize some functionalities
 * @param {boolean} [options.displayError] - Wether to display a generic error message or not
 */
const signOut = ({ displayError = true } = {}) => {
  displayError && notification.error({ message: t('EXPIRED_TOKEN') });
  TokenService.clearToken();
  window.location.replace(window.location.origin + "/#/login");
};

const callLatestRequest = ({ urlInfo, params, payload, resolve, reject }) => {
  self(urlInfo, params, payload)
    .then((retryData) => {
      return resolve(retryData);
    })
    .catch((retryError) => {
      notification.error({ message: retryError?.response?.data?.error?.message ?? t('TIME_OUT') });
      reject(retryError);
    });
};

const self = function dispatch (urlInfo, params = null, payload = null, options = { errorMessages: {} }) {
  let body;
  const url = generateUrl(urlInfo.url, params);
  if (urlInfo.method === "get") body = getDispatch;
  if (urlInfo.method === "post") body = postDispatch;
  if (urlInfo.method === "patch") body = patchDispatch;
  if (urlInfo.method === "put") body = putDispatch;
  if (urlInfo.method === "delete") body = deleteDispatch;

  return new Promise(function (resolve, reject) {
    body(url, payload)
      .then((data) => {
        return resolve(data.data);
      })
      .catch((err) => {
        if (
          err?.response?.status === 401 &&
          err?.response?.data?.error?.statusCode === "Token_001"
        ) {
          const refreshToken = TokenService.getRefreshToken();
          instance
            .post(`${BASE_URL}/token/refresh-token`, {
              refreshToken: refreshToken,
            })
            .then((res) => {
              TokenService.setToken(res.data);
              callLatestRequest({ urlInfo, params, payload, resolve, reject });
            })
            // refresh token error cases :
            .catch((tokenError) => {
              // expire refresh token: it means user has not logged in for 30 days:
              if (
                tokenError?.response?.status === 401 &&
                tokenError?.response?.data?.error?.statusCode === "Token_002"
              ) {
                signOut();
              }
              // prevent endless loop and duplication requests
              else if (
                tokenError?.response?.status === 401 &&
                tokenError?.response?.data?.error?.statusCode === "Token_005" &&
                tokenError?.config?.headers?.Authorization.replace(
                  "Bearer ",
                  ""
                ) !== TokenService.getAccessToken()
              ) {
                callLatestRequest({
                  urlInfo,
                  params,
                  payload,
                  resolve,
                  reject,
                });
              }
              // otherwise :
              else {
                setTimeout(() => {
                  signOut();
                }, 3000);
              }
            });
        }
        // multi device case , so sign out
        // we can't call logout api here because the access token has expired then
        // call refresh token and refresh token has expired too, so log out get error anyway.
        // but in login again we subscribe and unsubscribe fcm tokens in client. don't worry ;)
        else if (
          err?.response?.status === 401 &&
          err?.response?.data?.error?.statusCode === "Token_004"
        ) {
          setTimeout(() => {
            signOut();
          }, 3000);
        } else if (
          ['Token_008', 'USR_006', 'UserInfo_001'].includes(err?.response?.data?.error?.statusCode)
        ) {
          // User's token has been logged out, user could not be found, etc.
          notification.error({ message: getErrorMessage(err.response, options) });
          signOut({ displayError: false });
        } else {
          notification.error({ message: getErrorMessage(err.response, options) });
          reject(err.response);
        }
      });
  });
};

export default self;
